import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import EducationDrawer from 'components/EducationDrawer';
import {
  DrawerSection,
  DrawerSubtitle,
  VariableList,
  VariableItem,
  VariableName,
  VariableDescription,
  ExampleBox,
  ExampleLabel,
} from './StyledComponents';

const CodeBlock = styled.code`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.2)'
      : 'rgba(250, 70, 22, 0.1)'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 13px;
`;

const QuickVariablesDrawer = ({ isOpen, onClose }) => {
  const theme = useTheme();

  return (
    <EducationDrawer
      title="Quick Variables"
      isOpen={isOpen}
      onClose={onClose}
      showHelpButton={false}
    >
      <DrawerSection>
        <DrawerSubtitle>What are Quick Variables?</DrawerSubtitle>
        <p>
          Quick Variables are dynamic placeholders that automatically fill in
          with personalized information when your message is sent. This allows
          you to send personalized messages at scale without manually
          customizing each one.
        </p>
      </DrawerSection>

      <DrawerSection>
        <DrawerSubtitle>How They Work</DrawerSubtitle>
        <p>
          When you include a variable like{' '}
          <CodeBlock>{`{{first_name}}`}</CodeBlock> in your message, it will
          automatically be replaced with each recipient's actual first name when
          the message is sent.
        </p>
      </DrawerSection>

      <DrawerSection>
        <DrawerSubtitle>Available Campaign Variables</DrawerSubtitle>
        <VariableList>
          <VariableItem>
            <VariableName>{`{{first_name}}`}</VariableName>
            <VariableDescription>Recipient's first name</VariableDescription>
          </VariableItem>

          <VariableItem>
            <VariableName>{`{{event_name}}`}</VariableName>
            <VariableDescription>
              Name of the upcoming event
            </VariableDescription>
          </VariableItem>

          <VariableItem>
            <VariableName>{`{{event_date}}`}</VariableName>
            <VariableDescription>Date of the event</VariableDescription>
          </VariableItem>

          <VariableItem>
            <VariableName>{`{{discount_code}}`}</VariableName>
            <VariableDescription>
              Special promotional discount code
            </VariableDescription>
          </VariableItem>
        </VariableList>
      </DrawerSection>

      <DrawerSection>
        <DrawerSubtitle>Example Campaign Message</DrawerSubtitle>
        <ExampleBox>
          <ExampleLabel>You write:</ExampleLabel>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '12px',
            }}
          >
            <span>Hi</span>
            <CodeBlock>{`{{first_name}}`}</CodeBlock>
            <span>! Don't miss</span>
            <CodeBlock>{`{{event_name}}`}</CodeBlock>
            <span>on</span>
            <CodeBlock>{`{{event_date}}`}</CodeBlock>
            <span>at</span>
            <CodeBlock>{`{{track_name}}`}</CodeBlock>
            <span role="img" aria-label="checkered flag">
              🏁
            </span>
          </div>

          <ExampleLabel>Customer sees:</ExampleLabel>
          <div>
            Hi Sarah! Don't miss Summer Racing Series on July 15th at Thunder
            Valley Speedway{' '}
            <span role="img" aria-label="checkered flag">
              🏁
            </span>
          </div>
        </ExampleBox>
      </DrawerSection>

      <DrawerSection>
        <DrawerSubtitle
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <svg
            width="16"
            height="16"
            fill={theme?.colors?.primary || '#FA4616'}
            viewBox="0 0 16 16"
          >
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
          </svg>
          Pro Tips
        </DrawerSubtitle>
        <div
          style={{
            marginTop: '16px',
            color: '#CBD5E1',
            fontSize: '14px',
            lineHeight: '1.8',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '12px',
            }}
          >
            <span
              style={{
                color: theme?.colors?.primary || '#FA4616',
                marginRight: '12px',
                fontSize: '16px',
              }}
            >
              •
            </span>
            <span>
              Always preview your message to see how variables will appear
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '12px',
            }}
          >
            <span
              style={{
                color: theme?.colors?.primary || '#FA4616',
                marginRight: '12px',
                fontSize: '16px',
              }}
            >
              •
            </span>
            <span>
              Use emojis alongside variables for more engaging messages
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '12px',
            }}
          >
            <span
              style={{
                color: theme?.colors?.primary || '#FA4616',
                marginRight: '12px',
                fontSize: '16px',
              }}
            >
              •
            </span>
            <span>Keep messages concise - SMS has a 160 character limit</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <span
              style={{
                color: theme?.colors?.primary || '#FA4616',
                marginRight: '12px',
                fontSize: '16px',
              }}
            >
              •
            </span>
            <span>Test your messages with different customer data</span>
          </div>
        </div>
      </DrawerSection>
    </EducationDrawer>
  );
};

QuickVariablesDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QuickVariablesDrawer;
